/**
 * Softphone – moteur Amazon Connect : la bibliothèque officielle amazon-connect-streams charge le CCP de l'instance
 * dans une iframe masquée (le son passe par WebRTC dans cette iframe : `allowFramedSoftphone`) et publie l'agent et
 * ses contacts ; ce moteur les traduit dans le contrat commun (moteur.ts). Bibliothèque chargée à la demande : elle
 * (et le SDK Chime qu'elle embarque) n'alourdit pas le reste du front.
 * Connexion : SSO Entra (application SAML de Connect) dans une fenêtre ouverte par le bouton « Se connecter »
 * (connecter(), geste de l'utilisateur) ; jamais d'office au chargement.
 * Prérequis hors de ce fichier : origine approuvée dans l'instance ; CSP `frame-src` et Permissions-Policy
 * `microphone` ouverts à l'instance (infra/agora-web.yaml, paramètre SoftphoneOrigine) ; cookies tiers autorisés
 * pour le domaine de l'instance (stratégie Edge/Chrome).
 * Les types ci-dessous ne décrivent que la part de l'API Streams utilisée ici (tests avec une API simulée).
 */
/// <reference path="./streams.d.ts" />
import type { ConfigFront } from '@agora/shared';
import { creerDiffusion, numeroDuPoint, type CodeErreurSoftphone, type EtatAgent, type MoteurSoftphone } from './moteur';

interface Rappels { success?: () => void; failure?: (erreur?: unknown) => void }

interface ConnexionStreams {
  getEndpoint(): { phoneNumber?: string };
  isOnHold(): boolean;
  hold(r?: Rappels): void;
  resume(r?: Rappels): void;
  destroy(r?: Rappels): void;
  sendDigits(touches: string, r?: Rappels): void;
}

export interface ContactStreams {
  getContactId(): string;
  isInbound(): boolean;
  getStatus(): { type: string };
  getInitialConnection(): ConnexionStreams | null;
  getAgentConnection(): ConnexionStreams | null;
  accept(r?: Rappels): void;
  reject(r?: Rappels): void;
  clear(r: Rappels): void;
  addConnection(point: unknown, r?: Rappels): void;
  onConnected(f: () => void): void;
  onRefresh(f: () => void): void;
  onEnded(f: () => void): void;
}

interface EtatAgentStreams { name: string; type: string }

/** Configuration de l'agent : seuls `softphoneEnabled` et `extension` (téléphone fixe, E.164) sont modifiés ici. */
interface ConfigurationAgentStreams { softphoneEnabled: boolean; extension: string }

export interface AgentStreams {
  getState(): EtatAgentStreams;
  getAgentStates(): (EtatAgentStreams & { agentStateARN: string | null })[];
  setState(etat: unknown, r?: Rappels): void;
  getConfiguration(): ConfigurationAgentStreams;
  setConfiguration(configuration: ConfigurationAgentStreams, r?: Rappels): void;
  connect(point: unknown, r?: Rappels): void;
  onStateChange(f: () => void): void;
  onRefresh(f: () => void): void;
  onMuteToggle(f: (e: { muted: boolean }) => void): void;
  mute(): void;
  unmute(): void;
}

export interface ApiStreams {
  core: {
    initCCP(conteneur: HTMLElement, options: object): void;
    terminate(): void;
    onAuthFail(f: () => void): void;
    onAccessDenied(f: () => void): void;
  };
  agent(f: (a: AgentStreams) => void): void;
  contact(f: (c: ContactStreams) => void): void;
  Endpoint: { byPhoneNumber(numero: string): unknown };
}

export type OptionsConnect = NonNullable<ConfigFront['softphone']>;

/** Sans agent publié par le CCP après ce délai : session absente (SSO non fait, fenêtre bloquée). */
export const DELAI_CONNEXION_MS = 30_000;
/** Durée d'affichage d'un appel terminé avant de revenir au clavier. */
export const AFFICHAGE_FIN_MS = 2500;

/**
 * État Streams → état de l'agent. `system` (appel en cours, après-appel…) = occupé ; `error` (appel manqué par
 * l'agent, échec de connexion) est traité à part : le moteur remet l'agent dans l'état qu'il avait choisi.
 */
export function etatAgentDepuis(etat: EtatAgentStreams): EtatAgent {
  switch (etat.type) {
    case 'routable': return 'disponible';
    case 'not_routable':
    case 'offline': return 'absent';
    case 'init': return 'connexion';
    case 'error': return 'erreur';
    default: return 'occupe';
  }
}

async function chargerStreams(): Promise<ApiStreams> {
  await import('amazon-connect-streams');
  const api = (globalThis as unknown as { connect?: ApiStreams }).connect;
  if (!api?.core) throw new Error('amazon-connect-streams indisponible');
  return api;
}

export function creerMoteurConnect(options: OptionsConnect, charger: () => Promise<ApiStreams> = chargerStreams): MoteurSoftphone {
  const diffusion = creerDiffusion();
  let api: ApiStreams | undefined;
  let agent: AgentStreams | undefined;
  let contact: ContactStreams | undefined;
  let arrete = false;
  let disponibleVoulu = true;
  let attenteConnexion: ReturnType<typeof setTimeout> | undefined;
  let finAffichage: ReturnType<typeof setTimeout> | undefined;

  const signaler = (erreur: CodeErreurSoftphone) => () => diffusion.publier({ erreur });
  const enAppel = () => { const a = diffusion.lire().appel; return !!a && a.etat !== 'termine'; };

  const etatsCibles = (disponible: boolean) => {
    const etats = agent?.getAgentStates() ?? [];
    return disponible
      ? etats.find((e) => e.type === 'routable')
      : etats.find((e) => e.type === 'offline') ?? etats.find((e) => e.type === 'not_routable');
  };

  const publierAgent = () => {
    if (!agent || enAppel()) return;
    const etat = etatAgentDepuis(agent.getState());
    if (etat === 'erreur') {
      // Appel manqué ou échec de connexion : Connect bloque l'agent tant qu'il ne change pas d'état.
      const cible = etatsCibles(disponibleVoulu);
      if (cible) agent.setState(cible, { failure: signaler('action') });
      return;
    }
    if (etat === 'disponible' || etat === 'absent') disponibleVoulu = etat === 'disponible';
    diffusion.publier({ agent: contact ? 'occupe' : etat });
  };

  // Renvoi d'appel = téléphone fixe de l'agent dans Connect : softphone coupé, Connect appelle le numéro `extension`
  // pour chaque appel de l'agent (entrant, et premier temps d'un appel sortant). Relu à chaque rafraîchissement de
  // l'agent : un changement fait ailleurs (autre onglet, CCP) apparaît aussi.
  const publierRenvoi = () => {
    if (!agent) return;
    const c = agent.getConfiguration();
    const renvoi = !c.softphoneEnabled && c.extension ? c.extension : null;
    if (renvoi !== diffusion.lire().renvoi) diffusion.publier({ renvoi });
  };

  const suivreContact = (c: ContactStreams) => {
    // Contact déjà fini (page rechargée pendant l'après-appel) : on le referme sans l'afficher.
    if (['ended', 'missed', 'rejected', 'error'].includes(c.getStatus().type)) {
      c.clear({ failure: () => undefined });
      return;
    }
    clearTimeout(finAffichage);
    contact = c;
    const id = c.getContactId();
    const sens = c.isInbound() ? 'entrant' : 'sortant';
    const numero = () => numeroDuPoint(c.getInitialConnection()?.getEndpoint().phoneNumber);
    const courant = () => { const a = diffusion.lire().appel; return a?.id === id ? a : null; };
    const connecte = c.getStatus().type === 'connected';
    diffusion.publier({
      agent: 'occupe', erreur: undefined,
      appel: {
        id, sens, numero: numero(), etat: connecte ? 'en-cours' : sens === 'entrant' ? 'sonnerie' : 'numerotation',
        decrocheLe: connecte ? Date.now() : undefined, enAttente: false, muet: false,
      },
    });
    c.onConnected(() => {
      const a = courant();
      if (a && a.etat !== 'termine') diffusion.publier({ appel: { ...a, etat: 'en-cours', decrocheLe: a.decrocheLe ?? Date.now(), numero: a.numero ?? numero() } });
    });
    c.onRefresh(() => {
      const a = courant();
      const enAttente = !!c.getInitialConnection()?.isOnHold();
      if (a?.etat === 'en-cours' && a.enAttente !== enAttente) diffusion.publier({ appel: { ...a, enAttente } });
    });
    c.onEnded(() => {
      const a = courant();
      if (a && a.etat !== 'termine') diffusion.publier({ appel: { ...a, etat: 'termine', enAttente: false } });
      // Fin du travail après appel : l'agent redevient joignable sans passer par le CCP.
      c.clear({ failure: () => undefined });
      finAffichage = setTimeout(() => {
        if (contact === c) contact = undefined;
        if (courant()) diffusion.publier({ appel: null });
        publierAgent();
      }, AFFICHAGE_FIN_MS);
    });
  };

  const modifierAppel = (maj: { enAttente?: boolean; muet?: boolean }) => {
    const a = diffusion.lire().appel;
    if (a) diffusion.publier({ appel: { ...a, ...maj } });
  };

  return {
    demarrer(conteneur) {
      diffusion.publier({ agent: 'connexion', erreur: undefined });
      charger().then((streams) => {
        if (arrete) return;
        api = streams;
        streams.core.initCCP(conteneur, {
          ccpUrl: options.ccpUrl,
          region: options.region,
          ...(options.loginUrl ? { loginUrl: options.loginUrl } : {}),
          // Pas de fenêtre ouverte d'office à chaque chargement (bloquée hors geste de l'utilisateur) : le bouton
          // « Se connecter » ouvre la connexion Entra (connecter()), le CCP masqué se réessaie seul ensuite.
          loginPopup: false,
          softphone: { allowFramedSoftphone: true },
          pageOptions: { enableAudioDeviceSettings: false, enablePhoneTypeSettings: false },
        });
        streams.core.onAuthFail(() => diffusion.publier({ agent: 'deconnecte' }));
        streams.core.onAccessDenied(() => diffusion.publier({ agent: 'erreur', erreur: 'acces' }));
        streams.agent((a) => {
          agent = a;
          clearTimeout(attenteConnexion);
          publierAgent();
          publierRenvoi();
          a.onStateChange(publierAgent);
          a.onRefresh(publierRenvoi);
          a.onMuteToggle(({ muted }) => modifierAppel({ muet: muted }));
        });
        streams.contact(suivreContact);
        attenteConnexion = setTimeout(() => { if (!agent) diffusion.publier({ agent: 'deconnecte' }); }, DELAI_CONNEXION_MS);
      }, () => diffusion.publier({ agent: 'erreur', erreur: 'chargement' }));
    },
    arreter() {
      arrete = true;
      clearTimeout(attenteConnexion);
      clearTimeout(finAffichage);
      try { api?.core.terminate(); } catch { /* CCP déjà fermé */ }
    },
    abonner: diffusion.abonner,
    connecter() {
      diffusion.publier({ agent: 'connexion', erreur: undefined });
      window.open(options.loginUrl ?? options.ccpUrl, 'agora-connect', 'popup,width=420,height=640');
    },
    changerDisponibilite(oui) {
      disponibleVoulu = oui;
      const cible = etatsCibles(oui);
      if (agent && cible) agent.setState(cible, { failure: signaler('action') });
    },
    appeler(numero) {
      if (!agent || !api || enAppel()) return;
      diffusion.publier({ erreur: undefined });
      agent.connect(api.Endpoint.byPhoneNumber(numero), { failure: signaler('appel') });
    },
    decrocher() {
      if (diffusion.lire().appel?.etat === 'sonnerie') contact?.accept({ failure: signaler('action') });
    },
    raccrocher() {
      const a = diffusion.lire().appel;
      if (!contact || !a || a.etat === 'termine') return;
      if (a.etat === 'sonnerie') contact.reject({ failure: signaler('action') });
      else contact.getAgentConnection()?.destroy({ failure: signaler('action') });
    },
    basculerAttente() {
      const a = diffusion.lire().appel;
      const client = contact?.getInitialConnection();
      if (a?.etat !== 'en-cours' || !client) return;
      const enAttente = !a.enAttente;
      const r = { success: () => modifierAppel({ enAttente }), failure: signaler('action') };
      if (enAttente) client.hold(r);
      else client.resume(r);
    },
    basculerMuet() {
      const a = diffusion.lire().appel;
      if (a?.etat !== 'en-cours' || !agent) return;
      if (a.muet) agent.unmute();
      else agent.mute();
    },
    envoyerTouches(touches) {
      contact?.getAgentConnection()?.sendDigits(touches, { failure: signaler('action') });
    },
    transferer(numero) {
      const c = contact;
      if (!c || !api || diffusion.lire().appel?.etat !== 'en-cours') return;
      // Transfert « à froid » : le correspondant est joint, puis l'agent quitte l'appel.
      c.addConnection(api.Endpoint.byPhoneNumber(numero), {
        success: () => c.getAgentConnection()?.destroy(),
        failure: signaler('action'),
      });
    },
    definirRenvoi(numero) {
      if (!agent || enAppel()) return;
      const configuration = agent.getConfiguration();
      diffusion.publier({ erreur: undefined });
      // Connect refuse un numéro hors E.164 ou d'un pays non autorisé à l'appel sortant (erreur « renvoi »).
      agent.setConfiguration(
        { ...configuration, softphoneEnabled: !numero, extension: numero ?? configuration.extension },
        { success: () => diffusion.publier({ renvoi: numero }), failure: signaler('renvoi') },
      );
    },
  };
}
